// Login re-acceptance gate (Phase 3). When a user previously accepted the terms
// and their stored version is now stale (a version was bumped), this blocks the
// app with a non-dismissable modal until they re-accept.
//
// Existing users who never recorded consent (missing pointer) are NOT prompted
// — see needsReacceptance. New signups consent at Step 1, so the public
// onboarding flow (/signup) is always skipped here.

import { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'
// Full bundle, like every other httpsCallable caller: `firebase/app` alone does
// not register the .functions() accessor — it only worked here because another
// module happened to import it first.
import firebase from '../../firebase/compat'

import { Auth } from '../../StateManagment/Auth'
import { client } from '../../hooks/Client'
import Modal from '../common/Modal'
import needsReacceptance from '../../helpers/legal/needsReacceptance'
import { TERMS_URL, PRIVACY_URL, TERMS_VERSION, PRIVACY_VERSION } from '../../helpers/legal/legalDocs'

const ConsentGate = () => {

    const [authO] = useContext(Auth)
    const location = useLocation()

    const [accepted, setAccepted] = useState(false)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [done, setDone] = useState(false) // optimistic close after a successful save

    const onSignup = location.pathname.startsWith('/signup')
    const { needed } = needsReacceptance(authO, { termsVersion: TERMS_VERSION, privacyVersion: PRIVACY_VERSION })
    const open = needed && !onSignup && !done

    const confirm = async () => {
        if (!accepted) {
            setError('Je moet akkoord gaan met de algemene voorwaarden en het privacybeleid.')
            return
        }
        setSaving(true)
        setError('')
        try {
            // AI-CONTEXT: See docs/pitfalls.md#api-firebase-v1-region-default
            // recordConsent is a v2 callable in europe-west1. The default
            // firebase.functions() client targets us-central1 and would 404.
            const recordConsent = firebase.app().functions('europe-west1').httpsCallable('recordConsent')
            const result = await recordConsent({ data: { client } })
            if (result.data !== 'success') {
                throw new Error('Opslaan mislukt')
            }
            setDone(true)
        } catch (err) {
            console.error('ConsentGate recordConsent error', err)
            setError('Er ging iets mis bij het opslaan. Probeer het opnieuw.')
            setSaving(false)
        }
    }

    if (!open) return null

    const content = (
        <div className="consent-gate">
            <p>
                We hebben onze voorwaarden bijgewerkt. Lees ze door en ga akkoord
                om verder te gaan.
            </p>

            <div className="consent-gate-row">
                <input
                    type="checkbox"
                    id="consent-gate-accept"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                />
                <label htmlFor="consent-gate-accept">
                    Ik ga akkoord met de{' '}
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">algemene voorwaarden</a>
                    {' '}en het{' '}
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">privacybeleid</a>.
                </label>
            </div>

            {error && <p className="consent-gate-error">{error}</p>}

            <button
                type="button"
                className="consent-gate-button"
                onClick={confirm}
                disabled={saving}
            >
                {saving ? 'Bezig…' : 'Bevestigen'}
            </button>
        </div>
    )

    return (
        <Modal
            modalOpen={open}
            setModalOpen={() => {}} // never called: `blocking` disables every dismissal path
            pageHeader="Onze voorwaarden zijn bijgewerkt"
            content={content}
            blocking // no close button, no ESC, no backdrop — re-acceptance is mandatory
            actions={null} // no footer — the content renders its own confirm button
        />
    )
}

export default ConsentGate
